import React, { useMemo, useState, useCallback, useRef } from 'react';
import { 
  BarChartProps, 
  ThemeConfig, 
  PresetTheme,
  BarClickData,
  ChartDimensions,
} from '../types';
import { ChartContainer, Axis, Bar, Legend, Tooltip, Title } from './core';
import { 
  calculateScale, 
  getNumericValue, 
  valueToPosition,
} from '../utils/scales';
import { 
  getActiveTheme, 
  mergeAxisConfig, 
  mergeLegendConfig, 
  mergeTooltipConfig, 
  mergeAnimationConfig,
} from '../utils/defaults';
import { getDatasetColor, getHoverColor, resolveColor } from '../utils/colors';

export interface BarChartComponentProps extends BarChartProps {
  /** Preset theme name */
  theme?: PresetTheme;
  /** Custom theme configuration */
  themeConfig?: ThemeConfig;
}

/**
 * BarChart Component
 * 
 * A highly customizable, accessible bar chart built with pure SVG.
 * Supports vertical and horizontal orientations.
 * Fully responsive with mobile-optimized rendering.
 * 
 * @example
 * ```tsx
 * <BarChart
 *   labels={['Jan', 'Feb', 'Mar']}
 *   datasets={[
 *     { label: 'Sales', data: [100, 200, 150] }
 *   ]}
 *   borderRadius={8}
 *   theme="vibrant"
 * />
 * ```
 */
export const BarChart: React.FC<BarChartComponentProps> = ({
  labels,
  datasets,
  title,
  xAxis,
  yAxis,
  legend,
  tooltip,
  animation,
  width,
  height = 300,
  padding,
  backgroundColor,
  onBarClick,
  onBarHover,
  className,
  style,
  id,
  ariaLabel,
  borderRadius = 0,
  orientation = 'vertical',
  barGap = 2,
  groupGap = 0.2,
  showValues,
  valueLabels,
  theme = 'default',
  themeConfig,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{
    x: number;
    y: number;
    value: number | [number, number];
    label: string;
    datasetLabel: string;
    color: string;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());

  // Get theme
  const activeTheme = useMemo(() => {
    return getActiveTheme(theme, themeConfig);
  }, [theme, themeConfig]);

  // Get colors
  const colors = activeTheme.colors || [];

  // Filter visible datasets
  const visibleDatasets = useMemo(() => {
    return datasets.map((ds, i) => ({
      ...ds,
      hidden: ds.hidden || hiddenDatasets.has(i),
    }));
  }, [datasets, hiddenDatasets]);

  // Merge configs
  const xAxisConfig = useMemo(() => mergeAxisConfig(xAxis, undefined, activeTheme), [xAxis, activeTheme]);
  const yAxisConfig = useMemo(() => mergeAxisConfig(yAxis, undefined, activeTheme), [yAxis, activeTheme]);
  const legendConfig = useMemo(() => mergeLegendConfig(legend, undefined, activeTheme), [legend, activeTheme]);
  const tooltipConfig = useMemo(() => mergeTooltipConfig(tooltip, undefined, activeTheme), [tooltip, activeTheme]);
  const animationConfig = useMemo(() => mergeAnimationConfig(animation), [animation]);

  // Calculate scale
  const scale = useMemo(() => {
    const activeDatasets = visibleDatasets.filter(ds => !ds.hidden);
    return calculateScale(activeDatasets, {
      min: orientation === 'vertical' ? yAxis?.min : xAxis?.min,
      max: orientation === 'vertical' ? yAxis?.max : xAxis?.max,
      beginAtZero: orientation === 'vertical' ? yAxis?.beginAtZero : xAxis?.beginAtZero,
      maxTicks: (orientation === 'vertical' ? yAxis?.ticks?.maxTicksLimit : xAxis?.ticks?.maxTicksLimit) || 8,
    });
  }, [visibleDatasets, xAxis, yAxis, orientation]);

  // Handle bar hover with exact cursor position
  const handleBarHover = useCallback((
    e: React.MouseEvent, 
    data: BarClickData | null,
    mousePos?: { x: number; y: number }
  ) => {
    if (data) {
      setTooltipData({
        x: 0,
        y: 0,
        value: data.value as number | [number, number],
        label: data.label,
        datasetLabel: data.datasetLabel,
        color: resolveColor(
          datasets[data.datasetIndex].backgroundColor,
          data.dataIndex,
          getNumericValue(data.value),
          getDatasetColor(data.datasetIndex, colors)
        ),
        mouseX: mousePos?.x,
        mouseY: mousePos?.y,
      });
    } else {
      setTooltipData(null);
    }
    onBarHover?.(e, data);
  }, [datasets, colors, onBarHover]);

  // Toggle dataset visibility
  const handleToggleDataset = useCallback((index: number) => {
    setHiddenDatasets(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Generate accessible description
  const chartDescription = useMemo(() => {
    const datasetDescriptions = datasets.map((ds, i) => {
      const values = ds.data.map((v, j) => `${labels[j]}: ${getNumericValue(v)}`).join(', ');
      return `${ds.label}: ${values}`;
    }).join('. ');
    return `Bar chart showing ${datasets.length} dataset(s). ${datasetDescriptions}`;
  }, [datasets, labels]);

  const containerWrapperRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState<ChartDimensions | null>(null);

  return (
    <div ref={containerWrapperRef} style={{ position: 'relative' }}>
      <ChartContainer
        width={width}
        height={height}
        padding={padding}
        backgroundColor={backgroundColor || activeTheme.backgroundColor}
        className={className}
        style={style}
        id={id}
        ariaLabel={ariaLabel || chartDescription}
        title={typeof title === 'string' ? title : title?.text}
      >
        {(dimensions, containerSvgRef, isMobile) => {
          // Store ref for tooltip positioning
          (svgRef as any).current = containerSvgRef.current;
          // Store dimensions for tooltip
          if (!chartDimensions || chartDimensions.width !== dimensions.width) {
            setTimeout(() => setChartDimensions(dimensions), 0);
          }
        
        const { chartWidth, chartHeight } = dimensions;
        const isVertical = orientation === 'vertical';
        
        // Responsive bar gap
        const responsiveBarGap = isMobile ? Math.max(1, barGap * 0.5) : barGap;
        const responsiveGroupGap = isMobile ? Math.max(0.15, groupGap * 0.8) : groupGap;
        
        // Calculate bar dimensions
        const numGroups = labels.length;
        const numBars = visibleDatasets.filter(ds => !ds.hidden).length;
        const categorySize = isVertical ? chartWidth / numGroups : chartHeight / numGroups;
        const groupPadding = categorySize * responsiveGroupGap;
        const availableGroupSize = categorySize - groupPadding;
        const barSize = numBars > 0 ? (availableGroupSize - responsiveBarGap * (numBars - 1)) / numBars : availableGroupSize;

        // Render bars
        const bars: React.ReactNode[] = [];
        let visibleIndex = 0;

        visibleDatasets.forEach((dataset, datasetIndex) => {
          if (dataset.hidden) return;

          dataset.data.forEach((value, dataIndex) => {
            const numValue = getNumericValue(value);
            const baseValue = 0;
            
            // Calculate position
            const categoryStart = dataIndex * categorySize + groupPadding / 2;
            const barOffset = visibleIndex * (barSize + responsiveBarGap);
            
            // Calculate bar position and size
            let x: number, y: number, barWidth: number, barHeight: number;
            
            if (isVertical) {
              x = categoryStart + barOffset;
              barWidth = barSize;
              
              const valuePos = valueToPosition(numValue, scale, chartHeight, true);
              const basePos = valueToPosition(baseValue, scale, chartHeight, true);
              
              if (numValue >= 0) {
                y = valuePos;
                barHeight = basePos - valuePos;
              } else {
                y = basePos;
                barHeight = valuePos - basePos;
              }
            } else {
              y = categoryStart + barOffset;
              barHeight = barSize;
              
              const valuePos = valueToPosition(numValue, scale, chartWidth, false);
              const basePos = valueToPosition(baseValue, scale, chartWidth, false);
              
              if (numValue >= 0) {
                x = basePos;
                barWidth = valuePos - basePos;
              } else {
                x = valuePos;
                barWidth = basePos - valuePos;
              }
            }

            // Get colors
            const fillColor = resolveColor(
              dataset.backgroundColor,
              dataIndex,
              numValue,
              getDatasetColor(datasetIndex, colors)
            );
            const strokeColor = resolveColor(
              dataset.borderColor,
              dataIndex,
              numValue,
              fillColor
            );
            const hoverFillColor = resolveColor(
              dataset.hoverBackgroundColor,
              dataIndex,
              numValue,
              getHoverColor(fillColor)
            );

            // Get border radius (responsive)
            const baseRadius = dataset.borderRadius ?? borderRadius ?? activeTheme.borderRadius ?? 0;
            const radius = isMobile && typeof baseRadius === 'number' 
              ? Math.max(2, baseRadius * 0.6) 
              : baseRadius;

            bars.push(
              <Bar
                key={`${datasetIndex}-${dataIndex}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={dataset.borderWidth || 0}
                borderRadius={radius}
                opacity={dataset.opacity}
                animation={animationConfig}
                animationDelay={(animationConfig.staggerDelay || 0) * (datasetIndex * labels.length + dataIndex)}
                datasetIndex={datasetIndex}
                dataIndex={dataIndex}
                value={value}
                label={labels[dataIndex]}
                datasetLabel={dataset.label}
                onClick={onBarClick}
                onHover={handleBarHover}
                hoverFill={hoverFillColor}
                isMobile={isMobile}
              />
            );
          });

          visibleIndex++;
        });

        return (
          <>
            {/* Title */}
            <Title 
              config={title} 
              dimensions={dimensions}
              defaultColor={activeTheme.textColor}
              defaultFontFamily={activeTheme.fontFamily}
            />

            {/* Grid and Axes */}
            <Axis
              type="x"
              config={isVertical ? xAxisConfig : { ...xAxisConfig, ...yAxisConfig }}
              scale={scale}
              dimensions={dimensions}
              labels={isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />
            <Axis
              type="y"
              config={isVertical ? yAxisConfig : xAxisConfig}
              scale={scale}
              dimensions={dimensions}
              labels={!isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />

            {/* Bars */}
            <g className="bars" role="list" aria-label="Data bars">
              {bars}
            </g>

            {/* Value labels */}
            {showValues && visibleDatasets.map((dataset, datasetIndex) => {
              if (dataset.hidden) return null;
              
              let visIdx = 0;
              for (let i = 0; i < datasetIndex; i++) {
                if (!visibleDatasets[i].hidden) visIdx++;
              }

              return dataset.data.map((value, dataIndex) => {
                const numValue = getNumericValue(value);
                const categoryStart = dataIndex * categorySize + groupPadding / 2;
                const barOffset = visIdx * (barSize + responsiveBarGap);
                
                let x: number, y: number;
                
                if (isVertical) {
                  x = categoryStart + barOffset + barSize / 2;
                  const valuePos = valueToPosition(numValue, scale, chartHeight, true);
                  y = valuePos - 5;
                } else {
                  const valuePos = valueToPosition(numValue, scale, chartWidth, false);
                  x = valuePos + 5;
                  y = categoryStart + barOffset + barSize / 2;
                }

                const formattedValue = valueLabels?.formatter 
                  ? valueLabels.formatter(numValue)
                  : numValue.toLocaleString();

                const fontSize = isMobile 
                  ? Math.max(8, (valueLabels?.fontSize || 10) - 2)
                  : valueLabels?.fontSize || 10;

                return (
                  <text
                    key={`value-${datasetIndex}-${dataIndex}`}
                    x={x}
                    y={y}
                    textAnchor={isVertical ? 'middle' : 'start'}
                    dominantBaseline={isVertical ? 'auto' : 'middle'}
                    fill={valueLabels?.color || activeTheme.textColor}
                    fontSize={fontSize}
                    fontFamily={valueLabels?.fontFamily || activeTheme.fontFamily}
                    aria-hidden="true"
                  >
                    {formattedValue}
                  </text>
                );
              });
            })}

            {/* Legend */}
            <Legend
              config={legendConfig}
              datasets={visibleDatasets}
              colors={colors}
              dimensions={dimensions}
              onToggleDataset={handleToggleDataset}
              isMobile={isMobile}
            />

          </>
        );
      }}
    </ChartContainer>
      
      {/* Tooltip rendered outside SVG for proper positioning */}
      {tooltipConfig.enabled && tooltipData && chartDimensions && (
        <Tooltip
          config={tooltipConfig}
          data={tooltipData}
          dimensions={chartDimensions}
          containerRef={svgRef}
        />
      )}
    </div>
  );
};

export default BarChart;
